"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { Logos } from "./logos";

export function AuthLayout({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const mediaFrameStyle = {
    width: "max(100vw, calc(100svh * 16 / 9))",
    height: "max(100svh, calc(100vw * 9 / 16))",
  };

  return (
    <div className="flex min-h-svh relative">
      {/* Clip path: Vector 3 — right edge notches inward at ~82% x, ~72% y */}
      <svg className="size-0 absolute overflow-hidden" aria-hidden>
        <defs>
          <clipPath id="auth-panel-clip" clipPathUnits="objectBoundingBox">
            <path d="M0 1V0H1L0.8245 0.7245L1 1H0Z" />
          </clipPath>
        </defs>
      </svg>

      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        aria-hidden
      >
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          style={mediaFrameStyle}
        >
          {/* Full-bleed video background */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 size-full object-cover"
          >
            <source
              src="/assets/auth/auth_temp_video.webm"
              type='video/webm; codecs="av01"'
            />
            <source
              src="/assets/auth/auth_temp_video.hevc.mp4"
              type='video/mp4; codecs="hvc1"'
            />
            <source src="/assets/auth/auth_temp_video.mp4" type="video/mp4" />
          </video>

          {/* Figma overlay — node 9272:2 (Frame 26085122) */}
          <div className="absolute inset-0 pointer-events-none" aria-hidden>
            {/* Rectangle 6394 (9272:4) — three stacked vertical fades to #0d0d0d */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, rgba(13, 13, 13, 0) 36.538%, rgba(13, 13, 13, 0) 53.365%, rgb(13, 13, 13) 100%), linear-gradient(180deg, rgba(13, 13, 13, 0) 40.865%, rgba(13, 13, 13, 0) 61.538%, rgb(13, 13, 13) 100%), linear-gradient(180deg, rgba(13, 13, 13, 0) 37.981%, rgba(13, 13, 13, 0) 58.173%, rgb(13, 13, 13) 100%)",
              }}
            />
            {/* Rectangle 6394 (9272:4) — #ffaf53 color-blend tint */}
            <div
              className="absolute inset-0 mix-blend-color"
              style={{ backgroundColor: "#ffaf53" }}
            />

            {/* Group 613 (9272:6) — three overlay-blended vectors (9272:7/8/9), rotated 105.12° and Y-flipped */}
            {[
              "/assets/auth/overlay-vector-1.svg",
              "/assets/auth/overlay-vector-2.svg",
              "/assets/auth/overlay-vector-3.svg",
            ].map((src) => (
              <div
                key={src}
                className="absolute mix-blend-overlay flex items-center justify-center"
                style={{
                  left: "22.546%",
                  top: "-27.356%",
                  width: "106.909%",
                  height: "188.081%",
                }}
              >
                <div
                  style={{
                    transform: "rotate(105.12deg) scaleY(-1) scale(0.8)",
                    width: "90.260%",
                    height: "73.467%",
                  }}
                >
                  <img src={src} alt="" className="block size-full" />
                </div>
              </div>
            ))}

            {/* Vector (9272:10) — overlay-blended, rotated 62.38° */}
            <div
              className="absolute mix-blend-overlay flex items-center justify-center"
              style={{
                left: "-50.552%",
                top: "-160.207%",
                width: "141.090%",
                height: "259.367%",
              }}
            >
              <div
                style={{
                  transform: "rotate(62.38deg) scale(0.8)",
                  width: "93.734%",
                  height: "56.655%",
                }}
              >
                <img
                  src="/assets/auth/overlay-vector-4.svg"
                  alt=""
                  className="block size-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Left Panel — fixed to the viewport so the background stays put while
          the form scrolls internally on short screens (keeps the CTA reachable) */}
      <div className="w-full lg:w-160 lg:shrink-0 h-svh relative z-10">
        {/* Gradient background — clipped on desktop, full on mobile */}
        <div
          className="absolute inset-0 lg:hidden"
          style={{ background: "var(--auth-panel-gradient)" }}
        />
        <div
          className="absolute inset-0 hidden lg:block"
          style={{
            background: "var(--auth-panel-gradient)",
            clipPath: "url(#auth-panel-clip)",
          }}
        />
        {/* Border along the notched right edge */}
        <svg
          className="absolute inset-0 size-full hidden lg:block pointer-events-none text-border"
          viewBox="0 0 1 1"
          preserveAspectRatio="none"
        >
          <path
            d="M1 0L0.8245 0.7245L1 1"
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        </svg>

        {/* Scroll area: centers the form when it fits. Uses margin:auto (not
            justify-center) so when zoomed / short viewports overflow, content
            stays fully scrollable and the CTA remains reachable. */}
        <div className="relative size-full overflow-y-auto">
          <div className="min-h-full flex flex-col px-4 sm:px-16 py-6">
            <div className="my-auto w-full max-w-97 mx-auto lg:mx-0 relative">
              <AnimatePresence initial={false}>
                <motion.div
                  key={pathname}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2, ease: [0.2, 0, 0, 1] }}
                  className="flex flex-col gap-7"
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="hidden lg:flex min-w-0 flex-1 relative items-end z-10">
        <div className="w-full min-w-0 px-8 pb-8 xl:px-12">
          <div className="flex flex-col gap-1.5 items-end text-right mb-8">
            <Image
              src="/assets/auth/certifications.png"
              alt="ISO 27001, AICPA SOC 2 Type 2, GDPR certifications"
              width={660}
              height={140}
              className="mb-4 h-auto w-auto max-w-44"
            />
            <span className="font-canela text-3xl xl:text-4xl 2xl:text-5xl z-10! tracking-[-0.96px] text-white text-balance">
              Meet Arbi, The future of recruiting.
            </span>
            <p className="text-base xl:text-lg 2xl:text-xl text-white/80 tracking-tight max-w-full 2xl:max-w-190 font-normal leading-[1.3]">
              Arbi is the AI recruiting platform that transforms hiring into a
              science. Arbi unifies sourcing, sequencing, screening, and agents
              for world-class companies.
            </p>
          </div>

          <Logos />
        </div>
      </div>
    </div>
  );
}
